// src/routes/AdminRoute.js
import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  selectAuthToken,
  selectAuthIsAdmin,
  selectAuthIsMarketOwner,
  selectAuthIsMarketOwnerReadOnly,
  selectAuthIsForecaster,
} from '../slices/authSlice';

// Role ranking
const ROLE_RANK = {
  forecaster: 1,
  market_owner_read: 2,
  market_owner: 3,
  admin: 4,
};

// Helper: check permission requirement (string or array) against user's rank
const permissionSatisfied = (requiredPermission, userRank) => {
  if (!requiredPermission) return true;
  const checkOne = (perm) => {
    const r = ROLE_RANK[perm];
    if (!r) return false; // unknown permission
    return userRank >= r;
  };
  if (Array.isArray(requiredPermission)) {
    return requiredPermission.some(checkOne);
  }
  return checkOne(requiredPermission);
};

// AdminRoute now accepts an optional prop `requiredPermission` (string or array)
const AdminRoute = ({ requiredPermission = 'admin' }) => {
  const token = useSelector(selectAuthToken);
  const isAdmin = useSelector(selectAuthIsAdmin);
  const isMarketOwner = useSelector(selectAuthIsMarketOwner);
  const isMarketOwnerReadOnly = useSelector(selectAuthIsMarketOwnerReadOnly);
  const isForecaster = useSelector(selectAuthIsForecaster);

  if (!token) {
    return <Navigate to="/signin" replace />;
  }

  // compute user rank
  const userRank = isAdmin
    ? ROLE_RANK.admin
    : isMarketOwner
    ? ROLE_RANK.market_owner
    : isMarketOwnerReadOnly
    ? ROLE_RANK.market_owner_read
    : isForecaster
    ? ROLE_RANK.forecaster
    : 0;

  // if permission requirement is not satisfied, redirect
  if (!permissionSatisfied(requiredPermission, userRank)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Outlet />;
};

export default AdminRoute;